import { useEffect, useRef, useState } from 'react'

import { brand, nav } from '@/content/site'

function Wordmark() {
  return (
    <span className="font-sans text-base font-bold tracking-tight text-ink select-none">
      <span className="text-ink/30">/</span>
      {brand.wordmark}
    </span>
  )
}

export function NavBar() {
  const navRef = useRef<HTMLElement | null>(null)
  const lastScroll = useRef(0)
  const [open, setOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  // Entry animation → hide-on-scroll-down / reveal-on-scroll-up with a 100px
  // dead zone → transparent-to-glass past 80px. Plain JS, rAF-throttled.
  useEffect(() => {
    const el = navRef.current
    if (!el) return

    let frame = 0
    const onScroll = () => {
      const y = window.scrollY ?? document.documentElement.scrollTop ?? 0
      el.style.transform = y > lastScroll.current && y > 100 ? 'translateY(-100%)' : 'translateY(0)'
      setScrolled(y > 80)
      lastScroll.current = y
    }

    const schedule = () => {
      if (frame) return
      frame = window.requestAnimationFrame(() => {
        frame = 0
        onScroll()
      })
    }

    onScroll()
    window.addEventListener('scroll', schedule, { passive: true })
    return () => {
      window.removeEventListener('scroll', schedule)
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  return (
    <nav
      ref={navRef}
      className={`main-nav fixed inset-x-0 top-0 z-50 py-3${scrolled ? ' nav-scrolled' : ''}`}
    >
      <div className="shell flex items-center justify-between">
        <a href="#top" className="focus-ring flex items-center rounded-lg" aria-label={nav.logoAria}>
          <Wordmark />
        </a>

        <div className="hidden items-center gap-8 lg:flex">
          {nav.links.map((link) => (
            <a key={link.label} href={link.href} className="nav-link font-paragraph text-[0.9rem] tracking-wide">
              {link.label}
            </a>
          ))}
        </div>

        <div className="hidden items-center gap-4 md:flex">
          <a href={nav.cta.href} className="nav-cta focus-ring">
            {nav.cta.label}
          </a>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="mobile-navigation-menu"
          aria-label={open ? nav.menuCloseAria : nav.menuOpenAria}
          className="focus-ring inline-flex size-10 items-center justify-center rounded-lg border border-accent/20 bg-bg/80 text-ink lg:hidden"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            {open ? (
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            ) : (
              <path
                d="M4 7h16M4 12h16M4 17h16"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            )}
          </svg>
        </button>
      </div>

      {open && (
        <div
          id="mobile-navigation-menu"
          className="absolute left-4 right-4 top-[4.75rem] rounded-xl border border-accent/15 bg-bg/95 p-4 shadow-[0_20px_60px_rgba(0,0,0,0.35)] backdrop-blur-xl lg:hidden"
        >
          {nav.links.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={() => setOpen(false)}
              className="block rounded-lg px-3 py-3 font-paragraph text-sm text-ink-dim transition-colors hover:bg-accent/5 hover:text-ink"
            >
              {link.label}
            </a>
          ))}
          <a
            href={nav.cta.href}
            onClick={() => setOpen(false)}
            className="mt-3 block rounded-lg bg-accent px-3 py-3 text-center font-mono text-xs font-bold uppercase tracking-wider text-bg"
          >
            {nav.cta.label}
          </a>
        </div>
      )}
    </nav>
  )
}
